import { AwsClient } from 'aws4fetch';
import { getIdentityPoolCredentials } from './awsCredentials';
import { getValidIdToken } from './cognito';

// Client for the MAVIS API (app/backend/api-mavis-management). Every request is SigV4-signed with
// the current Cognito identity. Long-running actions answer 202 with a job id; `request` polls the
// job and resolves with the action's final result, so callers never see the job.

const API_URL = import.meta.env.VITE_API_URL;
const JOB_POLL_INTERVAL_MS = 2_000;
// Longer than the server's 20-minute job expiry, so the caller sees the job's real outcome
// (including a Lambda retry finishing it) rather than giving up first.
const JOB_TIMEOUT_MS = 21 * 60_000;

export class MavisApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: any,
  ) {
    super(typeof body?.error === 'string' ? body.error : body?.error?.message ?? 'MAVIS request failed.');
    this.name = 'MavisApiError';
  }
}

interface RequestOptions {
  signal?: AbortSignal;
}

async function send(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
  const token = await getValidIdToken();
  if (!token) throw new Error('Please sign in to continue.');
  const credentials = await getIdentityPoolCredentials(token);
  const client = new AwsClient({ ...credentials, region: import.meta.env.VITE_AWS_REGION, service: 'execute-api' });
  return client.fetch(`${API_URL}${path}`, {
    method,
    headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

async function readJson(response: Response): Promise<any> {
  return response.json().catch(() => ({}));
}

async function request<T>(method: string, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
  const response = await send(method, path, body, options.signal);
  const payload = await readJson(response);
  if (response.status === 202 && typeof payload?.jobId === 'string') return waitForJob<T>(payload.jobId, options.signal);
  if (!response.ok) throw new MavisApiError(response.status, payload);
  return payload as T;
}

async function waitForJob<T>(jobId: string, signal?: AbortSignal): Promise<T> {
  const deadline = Date.now() + JOB_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
    if (signal?.aborted) throw new DOMException('Action cancelled', 'AbortError');
    const job = await request<{ state: string; result: unknown; response_status: number | null }>('GET', `/v1/mavis/jobs/${jobId}`);
    if (job.state === 'completed') return job.result as T;
    if (job.state === 'failed') throw new MavisApiError(job.response_status ?? 500, job.result);
  }
  throw new Error('MAVIS is still processing. Refresh this page to see the saved result.');
}

const query = (params: Record<string, string | null | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
};

const opportunityPath = (opportunityId: string) => `/v1/mavis/opportunities/${encodeURIComponent(opportunityId)}`;
const meetingPath = (meetingId: string) => `/v1/mavis/meetings/${encodeURIComponent(meetingId)}`;

type Row = Record<string, any>;

// ── Opportunities ────────────────────────────────────────────────────────────

export const listAccounts = (orgId: string) =>
  request<{ accounts: { id: string; company_name: string }[] }>('GET', `/v1/mavis/accounts${query({ org_id: orgId })}`);

export const listOpportunities = (orgId: string, accountId?: string) =>
  request<{ opportunities: Row[] }>('GET', `/v1/mavis/opportunities${query({ org_id: orgId, account_id: accountId })}`);

export const createOpportunity = (opportunity: Row) =>
  request<{ opportunity: Row }>('POST', '/v1/mavis/opportunities', opportunity);

export interface OpportunityDetail {
  opportunity: Row;
  qualification: Row | null;
  stakeholders: Row[];
  meetings: Row[];
  contacts: Row[];
  hypothesis: Row | null;
  use_cases: { use_case_data: any; status: string } | null;
  triggers: Row[];
}

export const getOpportunity = (opportunityId: string) =>
  request<OpportunityDetail>('GET', opportunityPath(opportunityId));

export const updateOpportunity = (opportunityId: string, patch: Row) =>
  request<{ opportunity: Row }>('PUT', opportunityPath(opportunityId), patch);

export const saveQualification = (opportunityId: string, patch: Row) =>
  request<{ qualification: Row }>('PUT', `${opportunityPath(opportunityId)}/qualification`, patch);

export const createStakeholder = (opportunityId: string, stakeholder: Row) =>
  request<{ stakeholder: Row }>('POST', `${opportunityPath(opportunityId)}/stakeholders`, stakeholder);

export const updateStakeholder = (stakeholderId: string, patch: Row) =>
  request<{ stakeholder: Row }>('PUT', `/v1/mavis/stakeholders/${encodeURIComponent(stakeholderId)}`, patch);

export const deleteStakeholder = (stakeholderId: string) =>
  request<{ deleted: true }>('DELETE', `/v1/mavis/stakeholders/${encodeURIComponent(stakeholderId)}`);

export const listForecastAssessments = (opportunityId: string) =>
  request<{ assessments: Row[] }>('GET', `${opportunityPath(opportunityId)}/forecast-assessments`);

export const assessOpportunity = (opportunityId: string, options?: RequestOptions) =>
  request<Row>('POST', `${opportunityPath(opportunityId)}/forecast-assessments`, {}, options);

export const getPrepBrief = (opportunityId: string) =>
  request<{ brief: { id: string; brief_json: any; created_at: string } | null }>('GET', `${opportunityPath(opportunityId)}/prep-brief`);

export const generatePrepBrief = (opportunityId: string, options?: RequestOptions) =>
  request<Row>('POST', `${opportunityPath(opportunityId)}/prep-brief`, {}, options);

export const dealCoachChat = (opportunityId: string, body: { message: string; conversation_history: unknown }, options?: RequestOptions) =>
  request<Row>('POST', `${opportunityPath(opportunityId)}/chat`, body, options);

// ── Meetings and capture ─────────────────────────────────────────────────────

export const listMeetings = (opportunityId: string) =>
  request<{ meetings: Row[] }>('GET', `${opportunityPath(opportunityId)}/meetings`);

export const createMeeting = (opportunityId: string, meeting: { meeting_url: string; meeting_title: string }) =>
  request<{ session: Row }>('POST', `${opportunityPath(opportunityId)}/meetings`, meeting);

export const getLivePlaybook = (opportunityId: string, meetingId?: string) =>
  request<Row>('GET', `${opportunityPath(opportunityId)}/playbook${query({ meeting_id: meetingId })}`);

export const getMeeting = (meetingId: string) =>
  request<{ session: Row; opportunity: { id: string; title: string } }>('GET', meetingPath(meetingId));

export const getMeetingTranscript = (meetingId: string, cursor?: string | null) =>
  request<Row>('GET', `${meetingPath(meetingId)}/transcript${query({ cursor })}`);

export const inviteMeetingBot = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/bot`, {});

export const refreshMeetingBotStatus = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/bot/refresh`, {});

export const controlMeetingCapture = (meetingId: string, command: 'pause' | 'resume' | 'leave' | 'reinvite') =>
  request<Row>('POST', `${meetingPath(meetingId)}/controls`, { command });

export const listMeetingRecordings = (meetingId: string) =>
  request<Row>('GET', `${meetingPath(meetingId)}/recordings`);

export const reviewMeeting = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/review`, {});

export const askMeetingReview = (meetingId: string, body: Row, options?: RequestOptions) =>
  request<Row>('POST', `${meetingPath(meetingId)}/ask`, body, options);

export const processLivePlaybook = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/playbook/process`, {});

export const retryLivePlaybook = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/playbook/retry`, {});

export const updatePlaybookSpeakerSide = (meetingId: string, body: { speaker_key: string; side: string }) =>
  request<Row>('PUT', `${meetingPath(meetingId)}/playbook/speaker-side`, body);

export const updatePlaybookTask = (meetingId: string, taskId: string, body: { status: string; notes: string; revision?: number }) =>
  request<Row>('PUT', `${meetingPath(meetingId)}/playbook/tasks/${encodeURIComponent(taskId)}`, body);

// ── Meeting intelligence ─────────────────────────────────────────────────────

export const fetchMeetingArtifacts = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/artifacts/fetch`, {});

export const refreshMeetingEvents = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/events/refresh`, {});

export const processMeetingIntelligence = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/intelligence`, {});

export const generateMeetingSummary = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/summary`, {});

export const generateQualificationSuggestions = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/qualification-suggestions`, {});

// ── Live assist ──────────────────────────────────────────────────────────────

export const generatePrecallBrief = (meetingId: string) =>
  request<Row>('POST', `${meetingPath(meetingId)}/precall-brief`, {});

export const listLiveGuidance = (meetingId: string) =>
  request<{ events: Row[] }>('GET', `${meetingPath(meetingId)}/live-guidance`);

export const generateLiveGuidance = (meetingId: string, body: { force?: boolean; last_generated_at?: string }) =>
  request<Row>('POST', `${meetingPath(meetingId)}/live-guidance`, body);

export const updateLiveGuidanceStatus = (eventId: string, status: string) =>
  request<Row>('PUT', `/v1/mavis/live-guidance/${encodeURIComponent(eventId)}`, { status });

export const listResponseDrafts = (meetingId: string) =>
  request<{ drafts: Row[] }>('GET', `${meetingPath(meetingId)}/response-drafts`);

export const generateResponseDrafts = (meetingId: string, body: { force?: boolean; max_drafts?: number }) =>
  request<Row>('POST', `${meetingPath(meetingId)}/response-drafts`, body);

export const updateResponseDraftStatus = (draftId: string, status: string) =>
  request<Row>('PUT', `/v1/mavis/response-drafts/${encodeURIComponent(draftId)}`, { status });

export const listQualificationTracking = (meetingId: string) =>
  request<{ tracking: Row[] }>('GET', `${meetingPath(meetingId)}/qualification-tracking`);

export const updateQualificationTracking = (meetingId: string, body: { opportunity_id?: string; force?: boolean }) =>
  request<Row>('POST', `${meetingPath(meetingId)}/qualification-tracking`, body);

export const listConversation = (meetingId: string) =>
  request<{ messages: Row[] }>('GET', `${meetingPath(meetingId)}/conversation`);

export const askInMeeting = (meetingId: string, body: Row) =>
  request<Row>('POST', `${meetingPath(meetingId)}/conversation`, body);

export const saveMeetingCorrection = (meetingId: string, body: Row) =>
  request<Row>('POST', `${meetingPath(meetingId)}/corrections`, body);

export const answerFromKnowledge = (meetingId: string, body: Row) =>
  request<Row>('POST', `${meetingPath(meetingId)}/knowledge-answer`, body);

export const applySellerObservation = (meetingId: string, body: Row) =>
  request<Row>('POST', `${meetingPath(meetingId)}/seller-observations`, body);

export const listVoiceOutputs = (meetingId: string) =>
  request<{ voice_outputs: Row[] }>('GET', `${meetingPath(meetingId)}/voice-outputs`);

export const updateVoiceOutput = (outputId: string, patch: Row) =>
  request<{ voice_output: Row }>('PUT', `/v1/mavis/voice-outputs/${encodeURIComponent(outputId)}`, patch);
